import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, Music, Clock, FileAudio, ShoppingBag, Disc } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const [data, setData] = useState({ uploaded: [], purchased: [], bookings: [] });
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (!token) {
      navigate('/login');
      return;
    }
    
    setUser(JSON.parse(storedUser));
    
    const fetchFiles = async () => {
       try {
          const res = await axios.get(`${BACKEND_URL}/api/artist/files`, {
             headers: { Authorization: `Bearer ${token}` }
          });
          setData(res.data);
       } catch (e) {
          console.error(e);
       }
    };
    fetchFiles();
  }, [navigate]);

  if (!user) return null;

  // Format date helper
  const formatDate = (isoString) => {
      if (!isoString || isoString === 'remote') return 'À distance';
      try {
          return new Date(isoString).toLocaleDateString('fr-FR', {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
          });
      } catch (e) { return isoString; }
  };

  return (
    <div className="pt-24 pb-20 px-4 min-h-screen">
       <div className="max-w-6xl mx-auto">
          <div className="mb-12">
             <h1 className="text-3xl md:text-4xl font-bold mb-2">Bonjour, {user.name} 👋</h1>
             <p className="text-gray-400">Bienvenue dans votre espace artiste.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
             {/* Left Column: Mixes & Masters */}
             <div className="space-y-8">
                <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
                    <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                        <Disc className="text-purple-400" /> Vos Projets (Mix/Master)
                    </h2>
                    <div className="space-y-4">
                        {data.uploaded.map(file => (
                            <div key={file.id} className="flex items-center justify-between p-4 bg-black/40 rounded-xl border border-white/5 hover:border-purple-500/30 transition-colors">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 bg-purple-900/30 rounded-lg flex items-center justify-center text-purple-400">
                                        <FileAudio className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <div className="font-bold text-sm md:text-base">{file.name}</div>
                                        <div className="text-xs text-gray-500">{file.type} • {file.date}</div>
                                    </div>
                                </div>
                                <a href={file.download_url} download className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white">
                                    <Download className="w-5 h-5" />
                                </a>
                            </div>
                        ))}
                        {data.uploaded.length === 0 && (
                            <div className="text-center py-8 text-gray-500 bg-black/20 rounded-xl border border-dashed border-white/10">
                                <p>Aucun projet terminé pour le moment.</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
                   <h2 className="text-xl font-bold mb-4">Besoin d'aide ?</h2>
                   <p className="text-gray-400 text-sm mb-4">
                      Pour toute question sur vos fichiers ou vos sessions, contactez directement l'ingénieur son.
                   </p>
                   <button 
                     onClick={() => navigate('/contact')}
                     className="text-purple-400 font-bold hover:text-purple-300 text-sm"
                   >
                      Contacter le studio →
                   </button>
                </div>
             </div>

             {/* Right Column: Bookings & Purchased Beats */}
             <div className="space-y-8">
                
                {/* Bookings Section */}
                <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
                   <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                      <Clock className="text-pink-400" /> Vos Sessions
                   </h2>
                   
                   {data.bookings && data.bookings.length > 0 ? (
                       <div className="space-y-4">
                           {data.bookings.map(booking => (
                               <div key={booking.id} className="p-4 bg-black/40 rounded-xl border border-white/5 hover:border-pink-500/30 transition-colors">
                                   <div className="flex justify-between items-start mb-2">
                                       <span className="font-bold text-pink-200">{booking.service_type}</span>
                                       <span className={`text-xs px-2 py-1 rounded-full ${booking.status === 'confirmed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                           {booking.status === 'confirmed' ? 'Confirmé' : 'En attente'}
                                       </span>
                                   </div>
                                   <div className="text-sm text-gray-300">
                                       <div className="flex items-center gap-2 mb-1">
                                           <Clock className="w-3 h-3" />
                                           {formatDate(booking.date)} {booking.time_slot ? `à ${booking.time_slot}` : ''}
                                       </div>
                                       {booking.engineer && (
                                           <div className="text-xs text-gray-500">Avec {booking.engineer}</div>
                                       )}
                                   </div>
                               </div>
                           ))}
                       </div>
                   ) : (
                       <div className="p-6 bg-gradient-to-br from-purple-900/20 to-pink-900/20 rounded-xl border border-white/10 text-center">
                          <p className="text-gray-400 mb-2">Vous n'avez pas de session à venir.</p>
                          <button 
                            onClick={() => navigate('/booking')}
                            className="mt-4 px-6 py-2 bg-white text-black font-bold rounded-full hover:bg-gray-200 transition-colors"
                          >
                             Réserver une session
                          </button>
                       </div>
                   )}
                </div>

                {/* Purchased Beats */}
                <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
                    <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                        <ShoppingBag className="text-blue-400" /> Vos Instrumentales Achetées
                    </h2>
                    <div className="space-y-4">
                        {data.purchased.map(file => (
                            <div key={file.id} className="flex items-center justify-between p-4 bg-black/40 rounded-xl border border-white/5 hover:border-blue-500/30 transition-colors">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 bg-blue-900/30 rounded-lg flex items-center justify-center text-blue-400">
                                        <Music className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <div className="font-bold text-sm md:text-base">{file.name}</div>
                                        <div className="text-xs text-gray-500">{file.type} • {file.date}</div>
                                    </div>
                                </div>
                                <a href={file.download_url} download className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white">
                                    <Download className="w-5 h-5" />
                                </a>
                            </div>
                        ))}
                        {data.purchased.length === 0 && (
                            <div className="text-center py-8 text-gray-500 bg-black/20 rounded-xl border border-dashed border-white/10">
                                <p>Vous n'avez pas encore acheté d'instrumentale.</p>
                                <button 
                                    onClick={() => navigate('/beats')}
                                    className="mt-2 text-blue-400 hover:underline text-sm"
                                >
                                    Voir le catalogue
                                </button>
                            </div>
                        )}
                    </div>
                </div>
             </div>
          </div>
       </div>
    </div>
  );
};

export default Dashboard;
